import Image from 'next/image'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { VideoProps } from '~types'
import styles from './Video.module.scss'
import { useWindowSize } from 'utils/hooks'
import { useIsIntersecting } from 'utils/hooks/useIsIntersecting'
import { isIOS } from 'utils/device/isIOS'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { fetchVimeoVideo, getVimeoPictureByResolution, getVimeoVideoByRendition } from 'lib/vimeo'
import PlayIcon from '../../../public/assets/svg/play.svg'
import PauseIcon from '../../../public/assets/svg/pause.svg'
import MuteIcon from '../../../public/assets/svg/mute.svg'
import UnmuteIcon from '../../../public/assets/svg/unmute.svg'
import FullscreenIcon from '../../../public/assets/svg/arrows-output.svg'
import { FullscreenDocument, FullscreenElement } from './types'
import { useModalExists } from 'lib/context/modalContext'

enum VideoPlaybackMode {
  SUCCESS = 'success',
  FAILED = 'failed',
  LOADING = 'loading'
}

type VimeoData = {
  url?: string | null
  poster?: {
    url: string
    altText: string
  }
}

const Video = React.memo(
  ({
    autoPlay = true,
    backgroundVideoXl,
    backgroundVideoSm,
    className,
    posterImageXl,
    posterImageSm,
    videoLoop,
    externalVideoRef,
    preload = 'none',
    handlePlaybackFailed,
    onMediaLoad,
    isChildOfHeroCarousel = false,
    isPaused,
    vimeoIdXl,
    vimeoIdSm,
    allowPause,
    allowVolume,
    restartOnLeaveViewport = false,
    autoPlayDelay = 0,
    allowFullscreen = false,
    isInModal
  }: VideoProps) => {
    const internalVideoRef = useRef<HTMLVideoElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const backgroundVideoRef = externalVideoRef || internalVideoRef
    const [videoPlaybackMode, setVideoPlaybackMode] = useState<VideoPlaybackMode>(VideoPlaybackMode.LOADING)
    const [vimeoData, setVimeoData] = useState<{
      desktop?: VimeoData
      mobile?: VimeoData
    }>({})
    const [isVimeoDataLoaded, setIsVimeoDataLoaded] = useState<boolean>(false)
    const [isPlaying, setIsPlaying] = useState(true)
    const [isMuted, setIsMuted] = useState(true)
    const [hasUserPaused, setHasUserPaused] = useState(false)
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const [isDelayPassed, setIsDelayPassed] = useState(autoPlayDelay === 0)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [isResizing, setIsResizing] = useState(false)

    const { isBreakpoint: isTabletAndUp } = useWindowSize('sm', '>=')
    const { isAnyModalOpen } = useModalExists()
    // @TODO: Fix fullScreen functionality in iOS with webkitEnterFullscreen and webkitendfullscreen
    // Do not allow iOS fullscreen until this is resolved
    const shouldAllowFullScreen = !isIOS() && allowFullscreen

    const fallbackImage = vimeoData.desktop?.poster?.url
      ? { url: vimeoData.desktop.poster.url }
      : vimeoData.mobile?.poster?.url
      ? { url: vimeoData.mobile.poster.url }
      : posterImageXl ?? posterImageSm

    const fallbackVideo = vimeoData.desktop?.url
      ? { url: vimeoData.desktop.url }
      : vimeoData.mobile?.url
      ? { url: vimeoData.mobile.url }
      : backgroundVideoXl ?? backgroundVideoSm

    const currentVimeoData = isTabletAndUp ? vimeoData.desktop : vimeoData.mobile

    const image = currentVimeoData?.poster?.url
      ? { url: currentVimeoData?.poster.url }
      : (isTabletAndUp ? posterImageXl : posterImageSm) ?? fallbackImage

    const video = currentVimeoData?.url
      ? { url: currentVimeoData.url }
      : (isTabletAndUp ? backgroundVideoXl : backgroundVideoSm) ?? fallbackVideo

    // Lazy loading: detect when any portion of video is in viewport
    const isInViewportForLoading = useIsIntersecting({
      ref: containerRef,
      observerOptions: {
        root: null,
        rootMargin: '0px',
        threshold: 0.01
      }
    })

    // Autoplay trigger: detect when 50% of video is visible
    const isHalfVisible = useIsIntersecting({
      ref: containerRef,
      observerOptions: {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      }
    })

    const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
    const videoKey = `video-${video?.url}`

    // Centralized function to play video
    // ignoreDelay - If true, bypasses the autoPlayDelay check
    const playVideo = useCallback(
      async (ignoreDelay = false) => {
        if (!backgroundVideoRef.current) return

        // Check if delay has passed (only if autoPlayDelay is enabled)
        if (autoPlayDelay > 0) {
          if (!ignoreDelay && !isDelayPassed) return
        }

        try {
          await backgroundVideoRef.current.play()
          backgroundVideoRef.current.poster = ''
          setVideoPlaybackMode(VideoPlaybackMode.SUCCESS)
          handlePlaybackFailed?.(false)
          setIsPlaying(true)
        } catch (error) {
          console.warn(`Failed to Reproduce Video`, error)
          setVideoPlaybackMode(VideoPlaybackMode.FAILED)
          handlePlaybackFailed?.(true)
        }
      },
      [backgroundVideoRef, handlePlaybackFailed, autoPlayDelay, isDelayPassed]
    )

    const fetchSingleVimeoVideo = async (vimeoId: string): Promise<VimeoData> => {
      const vimeoData = await fetchVimeoVideo(vimeoId)
      const url = getVimeoVideoByRendition('1080p', vimeoData)
      const posterUrl = getVimeoPictureByResolution('1920x1080', vimeoData)

      return {
        url,
        poster: posterUrl ? { url: posterUrl, altText: 'vimeo-video' } : undefined
      }
    }

    const fetchVimeoData = useCallback(async () => {
      // If no Vimeo IDs are provided, skip fetching
      // and mark as loaded to proceed with fallback options
      if (!vimeoIdXl && !vimeoIdSm) {
        setIsVimeoDataLoaded(true)
        return
      }
      setVideoPlaybackMode(VideoPlaybackMode.LOADING)

      try {
        const results = await Promise.allSettled([
          vimeoIdXl ? fetchSingleVimeoVideo(vimeoIdXl) : Promise.resolve(null),
          vimeoIdSm ? fetchSingleVimeoVideo(vimeoIdSm) : Promise.resolve(null)
        ])

        const newVimeoData: {
          desktop?: VimeoData
          mobile?: VimeoData
        } = {}

        // Process desktop result
        if (results[0].status === 'fulfilled' && results[0].value) {
          newVimeoData.desktop = results[0].value
        } else if (vimeoIdXl) {
          console.warn(`Failed to fetch desktop Vimeo video for ID: ${vimeoIdXl}`)
        }
        // Process mobile result
        if (results[1].status === 'fulfilled' && results[1].value) {
          newVimeoData.mobile = results[1].value
        } else if (vimeoIdSm) {
          console.warn(`Failed to fetch mobile Vimeo video for ID: ${vimeoIdSm}`)
        }

        setVimeoData(newVimeoData)
        setIsVimeoDataLoaded(true)
      } catch (error) {
        console.warn('Error fetching Vimeo videos:', error)
        // Mark as loaded, continue with fallback options
        setIsVimeoDataLoaded(true)
      }
    }, [vimeoIdXl, vimeoIdSm])

    const toggleFullscreen = async (): Promise<void> => {
      const container = containerRef.current as FullscreenElement | null
      const doc = document as FullscreenDocument
      const videoElement = backgroundVideoRef.current

      if (!container || !videoElement) return

      const currentTime = videoElement.currentTime
      const wasPlaying = !videoElement.paused
      const wasMuted = videoElement.muted
      const volume = videoElement.volume

      try {
        if (!doc.fullscreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
          // Enter fullscreen
          if (doc.fullscreenElement === undefined && container.requestFullscreen) {
            await container.requestFullscreen()
          } else if (container.webkitRequestFullscreen) {
            /* Safari */
            await container.webkitRequestFullscreen()
          }
        } else {
          // Exit fullscreen
          if (doc.exitFullscreen) {
            await doc.exitFullscreen()
            /* Safari */
          } else if (doc.webkitExitFullscreen) {
            await doc.webkitExitFullscreen()
          }
        }

        // Restore video state after fullscreen change
        setTimeout(() => {
          videoElement.currentTime = currentTime
          videoElement.muted = wasMuted
          videoElement.volume = volume

          if (wasPlaying) {
            videoElement.play().catch(console.warn)
          }
        }, 100)
      } catch (error) {
        console.warn('Error attempting to toggle fullscreen:', error)
      }
    }
    // Listeners for fullscreen changes
    useEffect(() => {
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement)
      }

      document.addEventListener('fullscreenchange', handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.addEventListener('msfullscreenchange', handleFullscreenChange)

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        document.removeEventListener('msfullscreenchange', handleFullscreenChange)
      }
    }, [])

    // Fetch Vimeo data on mount
    useEffect(() => {
      fetchVimeoData()
    }, [fetchVimeoData])

    // Handle autoplay delay timer and reset
    useEffect(() => {
      // Reset delay when video leaves viewport (for carousel re-entry)
      if (!isHalfVisible && autoPlayDelay > 0) {
        setIsDelayPassed(false)
        return
      }

      // Start timer when video becomes half visible
      if (autoPlayDelay > 0 && !isDelayPassed && isHalfVisible) {
        const timer = setTimeout(() => {
          setIsDelayPassed(true)
        }, autoPlayDelay)

        return () => clearTimeout(timer)
      }
    }, [autoPlayDelay, isDelayPassed, isHalfVisible])

    // Lazy loading: Only load video when it enters viewport
    useEffect(() => {
      if (isInViewportForLoading && !shouldLoadVideo) {
        setShouldLoadVideo(true)
      }
    }, [isInViewportForLoading, shouldLoadVideo])

    // Reset video when it leaves viewport (for carousel)
    useEffect(() => {
      if (!isInViewportForLoading && restartOnLeaveViewport && backgroundVideoRef.current) {
        backgroundVideoRef.current.currentTime = 0
      }
    }, [isInViewportForLoading, restartOnLeaveViewport, backgroundVideoRef])

    // Sync isPlaying state with actual video playback events
    useEffect(() => {
      const videoElement = backgroundVideoRef.current
      if (!videoElement) return

      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)

      videoElement.addEventListener('play', handlePlay)
      videoElement.addEventListener('pause', handlePause)

      return () => {
        videoElement.removeEventListener('play', handlePlay)
        videoElement.removeEventListener('pause', handlePause)
      }
    }, [backgroundVideoRef])

    // Handle visibility-based play/pause
    useEffect(() => {
      const videoElement = backgroundVideoRef.current
      if (!videoElement || !isVimeoDataLoaded || !shouldLoadVideo || !isDataLoaded) return

      // Autoplay when 50% of the video is visible
      //    If no modals are open: background videos can play freely
      //    - OR if this video is inside a modal: it can play even when modals are open
      const shouldPlay = isHalfVisible && !isPaused && !hasUserPaused && (!isAnyModalOpen || isInModal) && !isResizing
      if (shouldPlay) {
        playVideo()
      } else {
        videoElement.pause()
      }
    }, [
      isDataLoaded,
      isHalfVisible,
      isPaused,
      hasUserPaused,
      isAnyModalOpen,
      isInModal,
      isVimeoDataLoaded,
      shouldLoadVideo,
      playVideo,
      backgroundVideoRef,
      isResizing
    ])

    // In some cases like the video carousel
    // the load event may not trigger if the component is created before
    // it enters the viewport. Force call load to trigger the isDataLoaded state update.
    useEffect(() => {
      if (shouldLoadVideo && backgroundVideoRef.current && video?.url) {
        backgroundVideoRef.current.load()
      }
    }, [shouldLoadVideo, video?.url, backgroundVideoRef])

    // Handle mute state separately from play/pause
    useEffect(() => {
      const videoElement = backgroundVideoRef.current
      if (!videoElement) return

      // Always mute when not visible or paused, otherwise use user preference
      videoElement.muted = !isHalfVisible || isPaused ? true : isMuted
    }, [isHalfVisible, isPaused, isMuted, backgroundVideoRef])

    // Cleanup on unmount
    useEffect(() => {
      const videoElement = backgroundVideoRef.current

      return () => {
        if (videoElement && !restartOnLeaveViewport) {
          videoElement.pause()
          videoElement.currentTime = 0
        }
      }
    }, [restartOnLeaveViewport, backgroundVideoRef])

    //  Prevents video playback attempts during active resizing to avoid conflicts
    useEffect(() => {
      let resizeTimeout: NodeJS.Timeout

      const handleResizeStart = () => {
        setIsResizing(true)
        clearTimeout(resizeTimeout)
      }

      const handleResizeEnd = () => {
        resizeTimeout = setTimeout(() => {
          setIsResizing(false)
        }, 500)
      }

      window.addEventListener('resize', handleResizeStart)
      window.addEventListener('resize', handleResizeEnd)

      return () => {
        window.removeEventListener('resize', handleResizeStart)
        window.removeEventListener('resize', handleResizeEnd)
        clearTimeout(resizeTimeout)
      }
    }, [])

    const togglePlay = () => {
      const videoEl = backgroundVideoRef.current
      if (!videoEl) return
      if (isPlaying) {
        videoEl.pause()
        setHasUserPaused(true)
      } else {
        // Use centralized playVideo function with ignoreDelay=true for manual play
        playVideo(true)
        setHasUserPaused(false)
      }
      setIsPlaying(!isPlaying)
    }

    const toggleMute = () => {
      const videoEl = backgroundVideoRef.current
      if (!videoEl) return
      videoEl.muted = !isMuted
      setIsMuted(!isMuted)
    }

    if (!image && !video) return null

    return (
      <div className={cx(styles['full-screen-video-container'], className)} ref={containerRef}>
        <div
          className={cx(
            styles['poster-container'],
            videoPlaybackMode === VideoPlaybackMode.SUCCESS ? styles.hidden : styles.visible
          )}
        >
          {image && (
            <Image
              alt={image.altText ?? ''}
              src={image.url}
              width='0'
              height='0'
              sizes='100vw'
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>

        <video
          key={videoKey}
          autoPlay={autoPlayDelay > 0 ? false : autoPlay}
          loop={videoLoop}
          ref={backgroundVideoRef}
          data-testid='video-element'
          onError={() => handlePlaybackFailed?.(true)}
          muted
          playsInline
          poster={videoPlaybackMode === VideoPlaybackMode.LOADING ? image?.url : ''}
          disableRemotePlayback
          preload={preload}
          onLoadedData={() => {
            setIsDataLoaded(true)
            onMediaLoad?.()
          }}
          className={cx(videoPlaybackMode === VideoPlaybackMode.SUCCESS ? styles.visible : styles.hidden)}
        >
          {/* Only load video source when component is in viewport */}
          {shouldLoadVideo && video && <source src={video.url} type='video/mp4' />}
        </video>

        <div
          className={cx([
            styles.controls,
            isChildOfHeroCarousel && styles.isChildOfHeroCarousel,
            shouldAllowFullScreen && styles.allowWidthFillAvailable
          ])}
        >
          {allowPause && (
            <button
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
              className={styles.controlButton}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          )}

          {allowVolume && (
            <button onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'} className={styles.controlButton}>
              {isMuted ? <MuteIcon /> : <UnmuteIcon />}
            </button>
          )}

          {shouldAllowFullScreen && (
            <button
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              className={cx(styles.controlButton, styles.fullScreenButton)}
            >
              <FullscreenIcon />
            </button>
          )}
        </div>
      </div>
    )
  }
)
Video.displayName = 'Video'
export default Video
